<template>
  <div
    :class="[
      'home_content',
      { result: isVisibleResultInfo && results.items.length },
    ]"
  >
    <template v-if="isLoading">
      <BaseLoading class="loading" />
    </template>
    <template v-else>
      <div class="home_content">
        <HeroSection
          v-model="searchValue"
          @search="onSearch"
          @skip="skip"
          @clear="onClear"
          :isError="isError"
          :resultsQuery="results.query"
          :resultsNumber="results.items.length"
        />
        <SearchResults
          v-if="isVisibleResultInfo"
          :results="results.items"
          :query="results.query"
          @select="onSelect"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";

import HeroSection from "./HeroSection.vue";
import { BaseLoading } from "@/shared/ui";

import "@/assets/icons/SVG/emcd.svg";
import "@/assets/icons/SVG/copy.svg";
import "@/assets/icons/SVG/share.svg";
import SearchResults from "./SearchResults.vue";

const store = useStore();
const router = useRouter();
const route = useRoute();

const isLoading = ref(false);
const isError = ref(false);
const searchValue = ref(route.query.q || "");
const results = ref({
  query: "",
  items: [],
});

const isVisibleResultInfo = computed(() => {
  return !isError.value && !isLoading.value && !!results.value.query;
});

const onSearch = async () => {
  const search = searchValue.value.trim();

  if (!search) {
    return;
  }

  const currentNetwork = computed(
    () => store.getters["explorer/getCurrentNetwork"],
  );

  router.push({
    query: { q: search, blockchain: currentNetwork.value },
  });

  isLoading.value = true;
  isError.value = false;
  results.value = {
    query: search,
    items: [],
  };

  try {
    const { results: searchResults } = await store.dispatch("explorer/SEARCH", {
      queryParam: search,
      currentNetwork: currentNetwork.value,
    });
    if (searchResults.length === 0) {
      router.push({ name: "NotFound", query: { search } });
    } else if (searchResults.length === 1) {
      const { network, type, value } = searchResults[0];

      // This ensures the back button returns to clean home page without re-triggering search.
      router.replace({
        path: `/${network}/${type}/${value}`,
      });
    } else {
      results.value.items = searchResults;
    }
  } catch (error) {
    isError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const skip = () => {
  isLoading.value = false;
  isError.value = false;
  searchValue.value = "";
};

const onSelect = (item) => {
  router.push({
    path: `/${item.network}/${item.type}/${item.value}`,
  });
};

const onClear = () => {
  isError.value = false;
  results.value = {
    query: "",
    items: [],
  };
  router.push({
    query: { q: searchValue.value },
  });
};

onMounted(() => {
  if (searchValue.value) {
    onSearch();
  }
  window.emcd.AnalyticsService.track("page_view", {
    event_type: "page_view",
    product: "explorer",
    screen: "search",
  });
});
</script>

<style lang="scss" scoped>
.home {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  height: max-content;
  width: 100%;

  &_content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
    height: 100%;
    margin-top: 90px;

    &.result {
      margin-top: 0;
      padding: 0 32px;

      .home_content {
        margin-top: 0;
        padding: 0 32px;
      }
    }
  }

  &_search-results {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.search {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 32px 0 40px;

  &_wrapper {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
  }
}

.info {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 28px 0 24px;

  &_left {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }

  &_right {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;
  }
}

.bold {
  font-weight: 600;
}

.button {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.icon {
  width: 16px;
  height: 16px;
}

.tx_details {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  gap: 12px;
  margin-bottom: 63px;
}

.loading {
  margin-top: 90px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 400px;
}

@media (max-width: 575px) {
  .home {
    &_content {
      margin-top: 62px;

      &.result {
        padding: 0 6px;

        .home_content {
          padding: 0 6px;
        }
      }
    }
  }
}
</style>
